import React, { useMemo, useState } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { signOut } from 'aws-amplify/auth';
import SEOComponent from '../components/SEOComponent';
import DeviceInfoCard from '../components/portal/DeviceInfoCard';
import DateBar from '../components/portal/DateBar';
import Section from '../components/portal/Section';
import ErrorBanner from '../components/portal/ErrorBanner';
import PlannedActions from '../components/portal/PlannedActions';
import HeatingSchedule from '../components/portal/HeatingSchedule';
import DaikinControlCard from '../components/portal/DaikinControlCard';
import ForecastChart from '../components/chart_components/ForecastChart';
import MeasuredChart from '../components/chart_components/MeasuredChart';
import SolarChart from '../components/chart_components/SolarChart';
import SolarKPICards from '../components/chart_components/SolarKPICards';
import { useDevices, useDayData } from '../hooks/useDataPortal';
import useHeatingSchedule from '../hooks/useHeatingSchedule';
import usePermissions, { permissionsFor } from '../hooks/usePermissions';
import { localToday } from '../utils/dateFormatting';
import { windowsFromEvents, deriveActions } from '../utils/schedule';

// The signed-in portal. Rendered only inside <Authenticator>, so no request
// fires before there is a session.
const PortalPage = () => {
    const devices = useDevices();
    const deviceId = devices.selectedDeviceId;
    const access = usePermissions();
    const can = permissionsFor(deviceId, access);
    const [date, setDate] = useState(localToday);
    // Sensing-only sites have no schedule, so don't fetch one.
    const schedule = useHeatingSchedule(can.hasControl ? deviceId : null);
    const day = useDayData({
        deviceId, date, scheduleVersion: schedule.version, showSolar: can.hasSolax, showForecast: can.hasControl,
    });

    const plannedWindows = useMemo(() => windowsFromEvents(schedule.events), [schedule.events]);
    const plannedActions = useMemo(() => deriveActions(plannedWindows), [plannedWindows]);

    if (devices.loaded && !devices.error && devices.devices.length === 0) {
        return <NoAccess />;
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <ErrorBanner message={devices.error} className="mb-4" />

            <div className="mb-4">
                <DeviceInfoCard
                    devices={devices.devices}
                    selectedDeviceId={deviceId}
                    onSelect={devices.setSelectedDeviceId}
                    deviceInfo={devices.deviceInfo}
                    onSave={devices.saveDevice}
                    canEdit={can.canEditDevice}
                />
            </div>

            <DateBar date={date} onChange={setDate} />

            <Section title={can.hasControl ? 'Temperature Forecast' : 'Room temperature and heating'}>
                <ErrorBanner message={day.errors.temperature && `Could not load readings: ${day.errors.temperature}`} className="mb-3" />
                <div style={{ height: '55vh' }}>
                    {can.hasControl ? (
                        <ForecastChart
                            forecastData={day.forecast}
                            heatingStatus={day.heatingStatus}
                            loading={day.loading}
                            selectedDate={date}
                            plannedWindows={plannedWindows}
                        />
                    ) : (
                        <MeasuredChart
                            measurements={day.measurements}
                            heatingStatus={day.heatingStatus}
                            loading={day.loading}
                            selectedDate={date}
                        />
                    )}
                </div>
            </Section>

            {can.hasSolax && (
            <Section title="Solar">
                <ErrorBanner message={day.errors.solax && `Could not load solar data: ${day.errors.solax}`} className="mb-3" />
                <SolarKPICards solaxData={day.solax} solarForecast={day.solarForecast} selectedDate={date} />
                <div style={{ height: '55vh' }}>
                    <SolarChart solaxData={day.solax} solarForecast={day.solarForecast} selectedDate={date} />
                </div>
            </Section>
            )}

            {/* Control sites only: the user sets when the building should be warm;
                the backend works out when to start heating and drives the units. */}
            {can.hasControl && (
            <Section
                title="Comfort Objectives"
                subtitle={can.canOperate
                    ? 'Set the target temperature and time. The system works out when to run the heating to get there.'
                    : 'When the building will be warm, and when the heating starts to get it there.'}
            >
                <PlannedActions actions={plannedActions} />
                <HeatingSchedule deviceId={deviceId} schedule={schedule} canEdit={can.canOperate} />
            </Section>
            )}

            {can.hasDaikin && (
                <section className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                    <DaikinControlCard canControl={can.canOperate} />
                </section>
            )}

            <div className="flex justify-center mt-4 mb-8">
                <button onClick={() => signOut()} className="text-gray-400 hover:text-gray-600 text-sm transition-colors">
                    Sign out
                </button>
            </div>
        </div>
    );
};

const NoAccess = () => (
    <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">No sites yet</h2>
        <p className="text-gray-500 mb-8">
            Your account isn't linked to a building yet. Ask your Rebel Heat contact to give you access.
        </p>
        <button onClick={() => signOut()} className="text-gray-400 hover:text-gray-600 text-sm">Sign out</button>
    </div>
);

export function DataPortal() {
    return (
        <>
            <SEOComponent
                title="Data Portal | Rebel Heat"
                description="Data Portal for viewing and managing data"
                url="https://www.rebelheat.co.uk/data-portal"
                image="https://www.rebelheat.co.uk/logo.png"
                noindex
            />
            <Authenticator hideSignUp>
                {() => <PortalPage />}
            </Authenticator>
        </>
    );
}

export default DataPortal;
